import {createEntityAdapter, EntityAdapter, EntityState} from '@ngrx/entity';
import {createReducer, on} from '@ngrx/store';
import {createFeature, createSelector} from '@ngrx/store';
import {DraftJobChanges} from '../../models/draft-job-changes.model';
import {DraftJobChangesFilter} from '../../models/draft-job-changes-filter.model';
import {DraftJobChangeAvailability} from '../../types/draft-job-change-availability.enum';
import {PlanningDraftJobChangesApiActions} from './planning-draft-job-changes.actions';

export interface PlanningDraftJobChangesState extends EntityState<DraftJobChanges> {
  totalElements: number;
  page: number;
  selectedIds: string[];
  // True once the user clicks "select all" with more matching changes than are loaded: apply/dismiss
  // then send the current filter (no `ids`) so the server acts on every matching change, loaded or
  // not. An apply/dismiss filter without `ids` means "everything matching" server-side (confirmed
  // against DraftJobChangesController/ApplyDraftJobChangesByAdminUseCase in bringoz-server).
  selectAllMatching: boolean;
  filters: DraftJobChangesFilter;
  loading: boolean;
  scanning: boolean;
  applying: boolean;
  error: any;
}

const adapter: EntityAdapter<DraftJobChanges> = createEntityAdapter<DraftJobChanges>();

const initialState: PlanningDraftJobChangesState = adapter.getInitialState({
  totalElements: 0,
  page: 0,
  selectedIds: [],
  selectAllMatching: false,
  filters: {},
  loading: false,
  scanning: false,
  applying: false,
  error: null
});

const planningDraftJobChangesReducer = createReducer(
  initialState,

  on(PlanningDraftJobChangesApiActions.clearStore, () =>
    adapter.removeAll({...initialState})
  ),

  on(PlanningDraftJobChangesApiActions.scanDraftJobChanges, (state) => ({...state, scanning: true, error: null})),
  on(PlanningDraftJobChangesApiActions.scanDraftJobChangesSuccess, (state) => ({...state, scanning: false})),
  on(PlanningDraftJobChangesApiActions.scanDraftJobChangesFailure, (state, {error}) => ({...state, scanning: false, error})),

  on(PlanningDraftJobChangesApiActions.getDraftJobChangesPage, (state) => ({...state, loading: true, error: null})),
  // Infinite scroll: page 0 starts a fresh list (first load or a new filter/search), later pages
  // are appended. selectedIds is deliberately left alone — a selection must survive loading more
  // rows and changing the filter/search, so it can span rows that aren't currently loaded.
  on(PlanningDraftJobChangesApiActions.getDraftJobChangesPageSuccess, (state, {list, totalElements, page}) =>
    page === 0
      ? adapter.setAll(list, {...state, totalElements, page, loading: false})
      : adapter.upsertMany(list, {...state, totalElements, page, loading: false})
  ),
  on(PlanningDraftJobChangesApiActions.getDraftJobChangesPageFailure, (state, {error}) => ({...state, loading: false, error})),

  on(PlanningDraftJobChangesApiActions.applyDraftJobChanges, (state) => ({...state, applying: true, error: null})),
  on(PlanningDraftJobChangesApiActions.applyDraftJobChangesSuccess, (state) => ({...state, applying: false})),
  on(PlanningDraftJobChangesApiActions.applyDraftJobChangesFailure, (state, {error}) => ({...state, applying: false, error})),

  on(PlanningDraftJobChangesApiActions.dismissDraftJobChanges, (state) => ({...state, loading: true, error: null})),
  on(PlanningDraftJobChangesApiActions.dismissDraftJobChangesSuccess, (state, {filter}) =>
    filter?.ids?.length
      // Explicit-ids dismiss: those rows are gone, remove just them locally.
      ? adapter.removeMany(filter.ids, {...state, loading: false, selectedIds: [], selectAllMatching: false})
      // Filter-only dismiss ("all matching"): the loaded rows are only part of what matched this
      // filter, and the server just deleted all of it — so the matching set is now empty.
      : adapter.removeAll({...state, loading: false, selectedIds: [], selectAllMatching: false, totalElements: 0})
  ),
  on(PlanningDraftJobChangesApiActions.dismissDraftJobChangesFailure, (state, {error}) => ({...state, loading: false, error})),

  on(PlanningDraftJobChangesApiActions.setSelectedIds, (state, {selectedIds}) => ({...state, selectedIds, selectAllMatching: false})),
  on(PlanningDraftJobChangesApiActions.setSelectAllMatching, (state, {selectAllMatching}) => ({...state, selectAllMatching})),
  on(PlanningDraftJobChangesApiActions.setFilters, (state, {filters}) => ({...state, filters}))
);

export const planningDraftJobChangesFeature = createFeature({
  name: 'planningDraftJobChanges',
  reducer: planningDraftJobChangesReducer,
  extraSelectors: ({selectPlanningDraftJobChangesState}) => {
    const {selectAll, selectEntities} = adapter.getSelectors(selectPlanningDraftJobChangesState);
    return {
      selectAll,
      selectEntities,
      selectTotalElements: createSelector(selectPlanningDraftJobChangesState, (state) => state.totalElements),
      selectPage: createSelector(selectPlanningDraftJobChangesState, (state) => state.page),
      selectSelectedIds: createSelector(selectPlanningDraftJobChangesState, (state) => state.selectedIds),
      selectSelectAllMatching: createSelector(selectPlanningDraftJobChangesState, (state) => state.selectAllMatching),
      selectFilters: createSelector(selectPlanningDraftJobChangesState, (state) => state.filters),
      selectLoading: createSelector(selectPlanningDraftJobChangesState, (state) => state.loading),
      selectScanning: createSelector(selectPlanningDraftJobChangesState, (state) => state.scanning),
      selectApplying: createSelector(selectPlanningDraftJobChangesState, (state) => state.applying),
      // BLOCKED/UNAVAILABLE changes must never be selectable for apply — used to drive "select all".
      selectSelectableIds: createSelector(
        selectAll,
        (changes) => changes
          .filter((change) => change.availability !== DraftJobChangeAvailability.UNAVAILABLE)
          .map((change) => change.id)
      )
    };
  }
});
